import React, { useCallback, useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./components/assets/css/LiveChat.css";
import "./custom.css";

import api from './services/api';
import ErrorBoundary from './components/common/ErrorBoundary';
import SafeHTML from './components/common/SafeHTML';
import Splash from './splash/Splash';
import APIS from './components/pages/mobile/common/apis';
import GoToTop from './components/common/GoTop';
import { Modal } from "react-bootstrap";
import Maintenance from './components/pages/mobile/common/Maintenance';

// ── Lazy-loaded routes for code splitting ────────────────────────
const Home = lazy(() => import('./components/pages/mobile/Home'));
const Login = lazy(() => import('./components/pages/mobile/Login'));
const SignUp = lazy(() => import('./components/pages/mobile/SignUp'));
const Registion = lazy(() => import('./components/pages/desktop/Registion'));
const MAccount = lazy(() => import('./components/pages/mobile/MAccount'));
const Profile = lazy(() => import('./components/pages/mobile/Profile'));
const MDeposit = lazy(() => import('./components/pages/mobile/MDeposit'));
const MWithdrawal = lazy(() => import('./components/pages/mobile/MWithdrawal'));
const BettingRecord = lazy(() => import('./components/pages/mobile/BettingRecord'));
const TransactionRecord = lazy(() => import('./components/pages/mobile/TransactionRecord'));
const TurnoverRecord = lazy(() => import('./components/pages/mobile/TurnoverRecord'));
const RefrrelProgram = lazy(() => import('./components/pages/mobile/RefrrelProgram'));
const ResetPassword = lazy(() => import('./components/pages/mobile/ResetPassword'));
const MobileInbox = lazy(() => import('./components/pages/mobile/MobileInbox'));
const VIPConvert = lazy(() => import('./components/pages/mobile/VIPConvert'));
const Games = lazy(() => import('./components/pages/mobile/Games'));
const GameRun = lazy(() => import('./components/pages/mobile/GameRun'));
const MobilePromotion = lazy(() => import('./components/pages/mobile/MobilePromotion'));
const ChatHome = lazy(() => import('./components/Chat/ChatHome'));
const ChatHistory = lazy(() => import('./components/Chat/ChatHistory'));
const ChatBox = lazy(() => import('./components/Chat/ChatBox'));
const EXHome = lazy(() => import('./components/exchanges/EXHome'));
const EXHomeVelki = lazy(() => import('./components/exchanges/EXHomeVelki'));
const MatchOdds = lazy(() => import('./components/exchanges/MatchOdds'));
const ExAccount = lazy(() => import('./components/exchanges/ExAccount'));
const InPlay = lazy(() => import('./components/exchanges/InPlay'));
const PaymentTimer = lazy(() => import('./components/pages/mobile/common/PaymentTimer'));
const ForgotPassword = lazy(() => import('./components/pages/mobile/ForgotPassword'));
const StoreCode = lazy(() => import('./StoreCodeComponents'));
const MakePayment = lazy(() => import('./components/pages/mobile/common/MakePayment'));
const FreeSpinPage = lazy(() => import('./free-spin/FreeSpinPage'));
const NineWiket = lazy(() => import('./components/pages/mobile/NineWiket'));

// ── Balance polling interval (30s instead of 10s) ─────────────────
const BALANCE_POLL_INTERVAL = 30000;

function App() {
  // ── Initialize CSRF protection on startup ────────────────────
  useEffect(() => { api.init(); }, []);

  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const [showContent, setShowContent] = useState(false);
  const [pwaIconUrl, setPwaIconUrl] = useState(null);
  const [pwaSiteName, setPwaSiteName] = useState(null);
  const location = useLocation();

  // ── /#/9wkt exit handler ────────────────────────────────────────
  useEffect(() => {
    if (window.location.href.includes('/#/9wkt')) {
      const token = localStorage.getItem('token');
      api.post('/api/9wkt/exit', {}).finally(() => {
        const cleanUrl = window.location.href.split('/#/9wkt')[0];
        window.location.replace(cleanUrl);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [globalData, setGlobalData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('error');
  const [refreshPvtData, setRefreshPvtData] = useState(false);

  const showAlert = useCallback((message = false, type = 'error') => {
    if (!message) {
      setShowModal(false);
      setModalMessage('');
      return;
    }
    setShowModal(true);
    setModalType(type || 'error');
    setModalMessage(message);
  }, []);

  // ── Favicon + PWA icon (uses centralized API cache) ─────────────
  const setFavicon = useCallback((url) => {
    if (!url) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = url;
  }, []);

  const [cssLoading, setCssLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const origin = window.location.hostname.replace(/^www\./, '');
    api.getLogo(origin).then((data) => {
      if (cancelled) return;
      const item = Array.isArray(data) ? data[0] : data;
      if (item?.favicon) {
        const faviconUrl = item.favicon.startsWith('http')
          ? item.favicon
          : `${process.env.REACT_APP_API_URL}/storage/${item.favicon}`;
        setFavicon(faviconUrl);
        const pwaIcon = item?.logo
          ? (item.logo.startsWith('http') ? item.logo : `${process.env.REACT_APP_API_URL}/storage/${item.logo}`)
          : faviconUrl;
        setPwaIconUrl(pwaIcon);
      }
    }).catch((err) => {
      if (!cancelled) console.error("Error fetching favicon:", err);
    });
    return () => { cancelled = true; };
  }, [setFavicon]);

  // ── General site data (uses centralized API cache) ──────────────
  useEffect(() => {
    let cancelled = false;
    api.getGeneral().then((data) => {
      if (cancelled || !data) return;
      const item = Array.isArray(data) ? data[0] : data;
      if (item) {
        document.title = item.site_title || document.title;
        setPwaSiteName(item.site_title);
        localStorage.setItem('whatsapp', item.whatsapp || '');
        localStorage.setItem('liveChat', item.live_chat || '');
        localStorage.setItem('intercom', item.intercom || '');
        localStorage.setItem('tawkId', item?.tawk_id || '');
      }
    }).catch((err) => {
      if (!cancelled) console.error("Error fetching General:", err);
    });
    return () => { cancelled = true; };
  }, []);

  // ── PWA Manifest ─────────────────────────────────────────────────
  useEffect(() => {
    if (!pwaSiteName && !pwaIconUrl) return;
    const manifestData = {
      short_name: pwaSiteName || document.title,
      name: pwaSiteName || document.title,
      start_url: '/',
      display: 'standalone',
      theme_color: '#000000',
      background_color: '#ffffff',
      icons: pwaIconUrl
        ? [
            { src: pwaIconUrl, sizes: '192x192', type: 'image/png' },
            { src: pwaIconUrl, sizes: '512x512', type: 'image/png' },
          ]
        : [],
    };
    const blob = new Blob([JSON.stringify(manifestData)], { type: 'application/json' });
    const manifestUrl = URL.createObjectURL(blob);
    let manifestLink = document.querySelector("link[rel='manifest']");
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    manifestLink.href = manifestUrl;
    return () => URL.revokeObjectURL(manifestUrl);
  }, [pwaSiteName, pwaIconUrl]);

  // ── Balance fetching (uses state, not direct DOM manipulation) ──
  const balanceRef = useRef(0);

  const fetchBalance = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const data = await api.getBalance();
      if (data?.balance !== undefined) {
        balanceRef.current = data.balance;
        localStorage.setItem('balance', data.balance);
        // Update DOM elements with class 'mainBalance' reactively via ref
        document.querySelectorAll('.mainBalance').forEach(el => {
          el.textContent = data.balance;
        });
      }
    } catch (error) {
      if (error.status === 401) {
        handleLogout();
      } else {
        console.error('Error fetching balance:', error);
      }
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    api.clearCache();
    setIsLoggedIn(false);
    window.location.href = '/';
  }, []);

  // ── Balance polling (30s interval, reduced from 10s) ────────────
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchBalance();
    const interval = setInterval(fetchBalance, BALANCE_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [isLoggedIn, fetchBalance]);

  // ── Private Route wrapper (memoized) ────────────────────────────
  const PrivateRoute = useCallback(({ element }) => {
    return isLoggedIn ? element : <Navigate to="/login" />;
  }, [isLoggedIn]);

  // ── CSS loading ──────────────────────────────────────────────────
  useEffect(() => {
    let extraCssLink;
    let isMounted = true;

    const loadExtraCss = () => {
      return new Promise((resolve, reject) => {
        if (location.pathname === "/make-payment") {
          extraCssLink = document.createElement("link");
          extraCssLink.rel = "stylesheet";
          extraCssLink.href = "./extra.css";
          extraCssLink.id = "extra-css";
          extraCssLink.onload = resolve;
          extraCssLink.onerror = reject;
          document.head.appendChild(extraCssLink);
        } else {
          resolve();
        }
      });
    };

    const loadOtherCss = async () => {
      await import("./components/assets/css/flipclock.css");
      await import("./components/assets/css/Mobile.css");
    };

    const loadAllCss = async () => {
      setCssLoading(true);
      try {
        await Promise.all([loadExtraCss(), loadOtherCss()]);
      } catch (err) {
        console.error("CSS load error:", err);
      } finally {
        if (isMounted) setCssLoading(false);
      }
    };

    loadAllCss();

    return () => {
      isMounted = false;
      const existingLink = document.getElementById("extra-css");
      if (existingLink) document.head.removeChild(existingLink);
    };
  }, [location.pathname]);

  // ── Google Tag Manager ───────────────────────────────────────────
  const [gtmId, setGtmId] = useState(null);

  useEffect(() => {
    const id = globalData?.general?.google_tag;
    if (!id) return;
    setGtmId(id);

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    }

    const oldScript = document.getElementById("gtm-script");
    if (oldScript) oldScript.remove();

    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);

    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    return () => {
      const s = document.getElementById("gtm-script");
      if (s) s.remove();
    };
  }, [globalData]);

  // ── Loading fallback for lazy routes ────────────────────────────
  const LoadingFallback = () => (
    <div className="splash-screen">
      <div className="three-dots-loading">
        <span></span><span></span><span></span>
      </div>
    </div>
  );

  return (
    <ErrorBoundary>
      <div className="App">
        {gtmId && (
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`}
              height="0" width="0"
              style={{ display: "none", visibility: "hidden" }}
              title="gtm"
            />
          </noscript>
        )}
        <GoToTop />
        <APIS setGlobalData={setGlobalData} refreshPvtData={refreshPvtData} />

        <Splash onSplashComplete={() => {
          setShowContent(true);
          setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
        }} />

        {cssLoading && <Splash />}

        {showContent && (
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={
                globalData?.general?.site_status == 0
                  ? <Maintenance />
                  : <Home globalData={globalData} />
              } />
              <Route path="/signup" element={<SignUp slidersMain={globalData.sliders?.main || []} />} />
              <Route path="/login" element={<Login />} />
              <Route path="/games" element={<Games />} />
              <Route path="/forgot-password" element={<ForgotPassword showAlert={showAlert} />} />
              <Route path="/account" element={
                <PrivateRoute element={
                  <MAccount fetchBalance={fetchBalance} userData={globalData.userData}
                    general={globalData.general || {}} sociallink={globalData.socialLinks || {}} />
                } />
              } />
              <Route path="/9wiket" element={<NineWiket />} />
              <Route path="/profile" element={<PrivateRoute element={<Profile />} />} />
              <Route path="/deposit" element={
                <PrivateRoute element={
                  <MDeposit usdtRate={globalData?.general?.usdt_rate}
                    hasEWallet={globalData.hasEWallet} userData={globalData.userData || []}
                    transactionData={globalData.transactions || []}
                    items={globalData.paymentMethods || []}
                    promotions={globalData.promotions?.activePromotion || []} />
                } />
              } />
              <Route path="/withdrawal" element={
                <PrivateRoute element={
                  <MWithdrawal general={globalData.general} hasEWallet={globalData.hasEWallet}
                    userData={globalData.userData || []}
                    transactionData={globalData.transactions || []}
                    items={globalData.paymentMethods || []} />
                } />
              } />
              <Route path="/betting-record" element={<PrivateRoute element={<BettingRecord />} />} />
              <Route path="/transaction-record" element={
                <PrivateRoute element={
                  <TransactionRecord setRefreshPvtData={setRefreshPvtData}
                    transactionData={globalData.transactions || []} />
                } />
              } />
              <Route path="/turnover-record" element={
                <PrivateRoute element={<TurnoverRecord turnovers={globalData.turnovers || []} />} />
              } />
              <Route path="/refrrel-program" element={
                <PrivateRoute element={<RefrrelProgram showAlert={showAlert} userData={globalData.userData} />} />
              } />
              <Route path="/reset-password" element={<PrivateRoute element={<ResetPassword showAlert={showAlert} />} />} />
              <Route path="/inbox" element={<PrivateRoute element={<MobileInbox />} />} />
              <Route path="/vip" element={<PrivateRoute element={<VIPConvert />} />} />
              <Route path="/gamerun" element={<PrivateRoute element={<GameRun />} />} />
              <Route path="/promotions" element={
                <PrivateRoute element={<MobilePromotion promotions={globalData.promotions?.allPromotions || []} />} />
              } />
              <Route path="/chat" element={<PrivateRoute element={<ChatHome />} />} />
              <Route path="/chat-list" element={<PrivateRoute element={<ChatHistory />} />} />
              <Route path="/chat-box/:chatId" element={<PrivateRoute element={<ChatBox />} />} />
              <Route path="/odds-view" element={<PrivateRoute element={<MatchOdds />} />} />
              <Route path="/exchange" element={<PrivateRoute element={<EXHome />} />} />
              <Route path="/velki" element={<PrivateRoute element={<EXHomeVelki />} />} />
              <Route path="/exchange-account" element={<PrivateRoute element={<ExAccount />} />} />
              <Route path="/exchange-inplay" element={<PrivateRoute element={<InPlay />} />} />
              <Route path="/payment-request" element={<PrivateRoute element={<PaymentTimer />} />} />
              <Route path="/referral_code" element={<StoreCode codeType="referral_code" />} />
              <Route path="/affiliate_code" element={<StoreCode codeType="affiliate_code" />} />
              <Route path="/agent_code" element={<StoreCode codeType="agent_code" />} />
              <Route path="/make-payment" element={
                <MakePayment hasEWallet={globalData.hasEWallet} userData={globalData.userData || []} />
              } />
              <Route path="/free-spin" element={<FreeSpinPage />} />
              <Route path="/register" element={<Registion />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {/* ── Modal with SafeHTML (XSS fix) ── */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  {modalType === 'success' ? 'Success' : modalType === 'info' ? 'Notice' : 'Error'}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <SafeHTML html={modalMessage} />
              </Modal.Body>
            </Modal>
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default App;
